// Static configuration for Ascendia Property Intelligence.
// IMPORTANT: All pricing comes from the existing estimator engine (GET /api/estimator-config).
// Nothing here invents prices — add-on ids below map to the REAL add-ons returned by that config.
import { ChefHat, Bath, BedDouble, Blinds, Flower2, Grid2x2 } from "lucide-react";

// Customer "need" -> existing estimator service key + the exact label used by the Contact form <select>.
export const NEEDS = [
  { key: "detail", label: "Property Detailing", desc: "Our highest, surface-by-surface finish.", estimatorKey: "Property Detailing", leadLabel: "Property Detailing" },
  { key: "move", label: "Move-In / Move-Out", desc: "An empty-property clean before or after a move.", estimatorKey: "Move-In / Move-Out", leadLabel: "Move-In / Move-Out Cleaning" },
  { key: "post", label: "Post-Construction", desc: "Fine dust & residue after building or renovation.", estimatorKey: "Post-Construction", leadLabel: "Post-Construction Cleaning" },
  { key: "deep", label: "Deep Clean", desc: "A thorough refresh of a lived-in home.", estimatorKey: "General Deep Clean", leadLabel: "General Deep Cleaning" },
  { key: "general", label: "General Clean", desc: "Routine upkeep and maintenance.", estimatorKey: "General Cleaning", leadLabel: "General Cleaning" },
];

export const PROPERTY_TYPES = ["Apartment", "House", "Penthouse", "Duplex", "Office", "Other"];

// Overall condition slider stops -> existing estimator condition_multiplier keys.
export const CONDITION_STOPS = [
  { key: "Light", label: "Pristine", desc: "Recently maintained, light everyday upkeep." },
  { key: "Moderate", label: "Lived-in", desc: "Noticeable build-up and typical wear." },
  { key: "Heavy", label: "Heavy use", desc: "Significant grime or long-neglected areas." },
  { key: "Post-Construction", label: "Post-construction", desc: "Fine dust, residue and specks throughout." },
];

// Per-area condition (informs complexity & scope emphasis — NOT price).
export const AREA_CONDITIONS = ["Light", "Moderate", "Heavy"];
export const WINDOW_COMPLEXITY = ["Standard", "Complex", "Very complex"];

export const DEADLINE_PRESETS = ["Flexible", "ASAP", "This week", "Specific date"];

// The three service tiers. Add-on ids reference the REAL estimator add-ons.
export const DETAIL_LEVELS = [
  { key: "essential", label: "Essential", blurb: "A clean, presentable finish across the visible surfaces of every room.", addons: [] },
  { key: "deep", label: "Deep", blurb: "Beyond the surface \u2014 interiors, build-up and the areas most people skip.", addons: ["window_tracks", "cabinet_interiors"] },
  { key: "ascendia", label: "Ascendia Detail", blurb: "Our signature surface-by-surface detailing \u2014 tracks, hinges, crevices and fixtures.", addons: ["window_tracks", "cabinet_interiors", "oven", "fridge", "steam"] },
];

// Property areas. `show` decides if the area appears based on the user's profile.
// `scope` tiers are descriptive (from Ascendia's detailing philosophy) — they do not set price.
export const AREAS = [
  {
    id: "kitchen", name: "Kitchen", Icon: ChefHat, show: () => true,
    scope: {
      essential: ["Countertops & backsplash", "Cabinet exteriors", "Sink & taps", "Stovetop surface"],
      deep: ["Cabinet & drawer interiors", "Degrease surfaces", "Appliance exteriors"],
      ascendia: ["Cabinet hinges & runners", "Kickboards & toe-kicks", "Appliance interiors", "Sink crevices & drain edge"],
    },
    micro: ["cabinet hinges", "drawer runners", "faucet bases", "sink edges"],
  },
  {
    id: "bathrooms", name: "Bathrooms", Icon: Bath, show: (s) => Number(s.bathrooms) > 0,
    scope: {
      essential: ["Toilet, sink & shower", "Mirrors", "Floor & tiles"],
      deep: ["Limescale & hard-water removal", "Grout & corners", "Glass screens"],
      ascendia: ["Fixture & tap bases", "Drains & crevices", "Toilet hinges", "Steam detail where safe"],
    },
    micro: ["faucet bases", "toilet hinges", "shower tracks", "drains", "corners/edges"],
  },
  {
    id: "bedrooms", name: "Bedrooms & Living", Icon: BedDouble, show: (s) => Number(s.rooms) > 0,
    scope: {
      essential: ["Floors & surfaces", "Shelves & sills", "Wardrobe exteriors"],
      deep: ["Wardrobe interiors", "Skirting / baseboards", "Doors & frames"],
      ascendia: ["Door tops & edges", "Light switches & sockets", "Corner & edge detail"],
    },
    micro: ["baseboards", "door tops", "corners/edges"],
  },
  {
    id: "windows", name: "Windows", Icon: Blinds, show: () => true,
    scope: {
      essential: ["Interior glass", "Sills"],
      deep: ["Frames", "Shutters / screens"],
      ascendia: ["Tracks", "Corners", "Hidden build-up in grooves"],
    },
    micro: ["window tracks", "corners/edges"],
  },
  {
    id: "balcony", name: "Balcony", Icon: Flower2, show: (s) => !!s.balcony,
    scope: {
      essential: ["Floor wash", "Railings"],
      deep: ["Glass balustrade", "Drainage points"],
      ascendia: ["Track & groove detail", "Corner build-up", "Exterior glass where safe"],
    },
    micro: ["window tracks", "drains", "corners/edges"],
  },
  {
    id: "floors", name: "Floors", Icon: Grid2x2, show: () => true,
    scope: {
      essential: ["Vacuum & mop"],
      deep: ["Edges & corners", "Under-furniture where accessible"],
      ascendia: ["Baseboard junctions", "Grout lines", "Threshold & transition strips"],
    },
    micro: ["baseboards", "corners/edges"],
  },
];

// The signature "What Ascendia Sees" micro-areas (often-overlooked detail points).
export const MICRO_AREAS = [
  "window tracks", "cabinet hinges", "faucet bases", "sink edges", "toilet hinges",
  "door tops", "baseboards", "drawer runners", "shower tracks", "drains", "corners/edges",
];

export const PREP_NOTES = [
  { t: "Access, water & electricity", d: "Please ensure we have safe access, running water and electricity on the day." },
  { t: "Best after construction", d: "For the sharpest result, schedule once building, installations and painting are complete." },
  { t: "Valuables & fragile items", d: "Clear away valuables and fragile items so the team can work efficiently and safely." },
  { t: "Parking & building access", d: "Let us know about parking or building entry so we arrive ready to start." },
];
